import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';
import { listPatientOrdersApi } from 'api/patient';
import { format, parseISO } from 'date-fns';
import { filter, map } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PatientCurrentOrdersDropdown = ({ mpi, currentOrderId }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (!mpi) return;
    setLoading(true);
    listPatientOrdersApi({ mpi })
      .then((results) => {
        setOrders(filter(results, (o) => o.orderId !== currentOrderId));
      })
      .finally(() => setLoading(false));
  }, [mpi, currentOrderId]);

  if (loading) {
    return <CircularProgress size={16} thickness={4} />;
  }

  if (orders.length === 0) return null;

  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        size='small'
        variant='outlined'
        endIcon={<ExpandMoreIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {orders.length} Other Ongoing Order{orders.length !== 1 ? 's' : ''}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ minWidth: 260, maxWidth: 380, maxHeight: 420, overflow: 'auto' }}>
          <List dense disablePadding>
            {map(orders, (order, idx) => (
              <Box key={order.orderId}>
                {idx > 0 && <Divider />}
                <ListItemButton
                  onClick={() => {
                    setAnchorEl(null);
                    navigate(`/orders/${order.orderId}`);
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        component='span'
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                      >
                        {order.createdAt && (
                          <Typography variant='caption' color='text.secondary' component='span'>
                            {format(parseISO(order.createdAt), 'MM/dd/yyyy')}
                          </Typography>
                        )}
                        <Typography variant='body2' fontWeight={600} component='span'>
                          {order.orderId}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      order.lineItems?.length > 0 ? (
                        <Box component='span' sx={{ display: 'flex', flexDirection: 'column' }}>
                          {map(order.lineItems, (li) => (
                            <Typography
                              key={li.lineItemId}
                              component='span'
                              variant='caption'
                              color='text.secondary'
                            >
                              {li.overriddenItemName || li.itemName}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        'No items'
                      )
                    }
                    secondaryTypographyProps={{ component: 'span' }}
                  />
                </ListItemButton>
              </Box>
            ))}
          </List>
        </Box>
      </Popover>
    </>
  );
};

PatientCurrentOrdersDropdown.propTypes = {
  mpi: PropTypes.string.isRequired,
  currentOrderId: PropTypes.string.isRequired,
};

export default PatientCurrentOrdersDropdown;
